import type { RuntimeBillingPlan, RuntimeBillingPlansResponse } from "./runtimeWorkspaceClient";

async function parseJson<T>(response: Response): Promise<T> {
  return (await response.json()) as T;
}

async function assertOk(response: Response, fallback: string): Promise<void> {
  if (response.ok) return;
  const data = await response.json().catch(() => undefined) as { error?: string; message?: string } | undefined;
  throw new Error(data?.error ?? data?.message ?? fallback);
}

export interface PublicCheckoutInput {
  planId: string;
  email: string;
  companyName: string;
  buyerName?: string;
  extraDepartmentCount?: number;
  extraUserCount?: number;
}

export async function fetchPublicPricingPlans(): Promise<RuntimeBillingPlansResponse> {
  const response = await fetch("/api/public/pricing/plans");
  await assertOk(response, "Pricing could not be loaded.");
  return parseJson<RuntimeBillingPlansResponse>(response);
}

export async function startPublicCheckout(input: PublicCheckoutInput): Promise<{ checkoutUrl: string; purchaseSessionId: string; message?: string }> {
  const response = await fetch("/api/public/checkout", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input)
  });
  await assertOk(response, "Checkout could not be started.");
  return parseJson<{ checkoutUrl: string; purchaseSessionId: string; message?: string }>(response);
}

export async function setupPurchasedAccount(token: string, password: string, name?: string): Promise<{ ok: boolean; message?: string; redirectTo?: string }> {
  const response = await fetch("/api/auth/setup-account", {
    method: "POST",
    headers: { "content-type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ token, password, name })
  });
  const data = await response.json().catch(() => ({})) as { ok?: boolean; message?: string; error?: string; redirectTo?: string };
  if (!response.ok) return { ok: false, message: data.message ?? data.error ?? "Account could not be created." };
  return { ok: Boolean(data.ok), message: data.message, redirectTo: data.redirectTo };
}

export function publicPlanBadge(plan: RuntimeBillingPlan, language: "de" | "en"): string {
  if (plan.id === "guided_digital_access_3m") return language === "en" ? "For scale-ups" : "Für Scale-ups";
  if (plan.id === "guided_digital_access_plus_6m") return language === "en" ? "For mid-market" : "Für Mid-Market";
  if (plan.id === "guided_digital_access_executive_12m") return "Enterprise";
  return language === "en" ? "Flexible monthly" : "Flexibel monatlich";
}
